import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  BooleanInput,
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stAvatarDefaults,
  stCreateRoom,
  stDeleteRoom,
  stIdentityUsers,
  stListAgents,
  stRoomMemberMutes,
  stSetRoomMemberMute,
  stSetRoomMuteAll,
  stUpdateRoom,
  StAgent,
  StAvatarDefaults,
  StIdentityUser,
  StI18nEntry,
  StMemberMute,
  StRoom,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { SocialTradingAvatarField } from '../SocialTradingAvatarField';
import { I18nFieldBlock } from '../I18nFieldBlock';
import { StPoolSettleConfirmContent } from '../StPoolSettleConfirmContent';
import { emptyLangPack, isLangPackComplete, packFromEntries, syncZh } from '../i18nLang';
import {
  StIdCell,
  StActionButton,
  StActionRow,
  StBooleanChip,
  StScrollTable,
  StStatusChip,
  stFormatDateTime,
  stText,
  useStConfirmAction,
} from '../StListUi';
import {
  ANNOUNCEMENT_MAX_BYTES,
  announcementByteLength,
  announcementEditLocked,
  announcementOverBudget,
  identityOwnerLabel,
  isOwnerKolRoleConflictMessage,
  isSmartMoneyRequiresPublicMessage,
  kolCannotOwnOfficialRoom,
  ownerOptionsForRoomType,
} from './helpers';
import './init';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ROOM_TYPE_CHOICES = [
  { id: 'OFFICIAL_LOBBY', name: 'OFFICIAL_LOBBY' },
  { id: 'OFFICIAL_AGENT', name: 'OFFICIAL_AGENT' },
  { id: 'KOL_ROOM', name: 'KOL_ROOM' },
];

const singleAgentRoomType = (type: string) => type === 'OFFICIAL_AGENT' || type === 'KOL_ROOM';

const ROOM_VISIBILITY_CHOICES = [
  { id: 'PUBLIC', name: 'PUBLIC' },
  { id: 'PRIVATE', name: 'PRIVATE' },
];

const SMART_MONEY_FILTER_CHOICES = [
  { id: 'true', name: 'module.socialTrading.roomSmartMoneyOn' },
  { id: 'false', name: 'module.socialTrading.roomSmartMoneyOff' },
];

type RoomForm = {
  ownerUserId: string;
  type: string;
  name: string;
  tagline: string;
  lang: string;
  visibility: string;
  discoverable: boolean;
  smartMoney: boolean;
  announcement: string;
  announcementUpdatedAt: string;
  avatar: string;
  preview: string;
  max: string;
  selectedAgents: StAgent[];
  nameLang: StI18nEntry[];
  taglineLang: StI18nEntry[];
  previewLang: StI18nEntry[];
  announcementLang: StI18nEntry[];
  nameLangDirty: boolean;
  taglineLangDirty: boolean;
  previewLangDirty: boolean;
  announcementLangDirty: boolean;
  i18nStale: boolean;
};

const emptyForm = (): RoomForm => ({
  ownerUserId: '',
  type: 'OFFICIAL_LOBBY',
  name: '',
  tagline: '',
  lang: 'ZH',
  visibility: 'PUBLIC',
  discoverable: true,
  smartMoney: false,
  announcement: '',
  announcementUpdatedAt: '',
  avatar: '',
  preview: '',
  max: '500',
  selectedAgents: [],
  nameLang: emptyLangPack(),
  taglineLang: emptyLangPack(),
  previewLang: emptyLangPack(),
  announcementLang: emptyLangPack(),
  nameLangDirty: false,
  taglineLangDirty: false,
  previewLangDirty: false,
  announcementLangDirty: false,
  i18nStale: false,
});

function toISO(local: string) {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  return d.toISOString();
}

function toLocalInputValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function roomDefaultUrls(defaults: StAvatarDefaults | null, type: string): string[] {
  if (!defaults) return [];
  const url =
    type === 'OFFICIAL_LOBBY'
      ? defaults.roomOfficial
      : type === 'OFFICIAL_AGENT'
        ? defaults.roomAgent
        : defaults.roomKol;
  return url ? [url] : [];
}

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <TextInput source="roomId" label={t('module.socialTrading.colRoomId')} alwaysOn resettable />
      <TextInput source="roomName" label={t('module.socialTrading.colRoomName')} alwaysOn resettable />
      <SelectInput
        source="type"
        label={t('module.socialTrading.colType')}
        choices={ROOM_TYPE_CHOICES}
        alwaysOn
        resettable
        emptyText={t('module.socialTrading.filterAll')}
      />
      <SelectInput
        source="visibility"
        label={t('module.socialTrading.colVisibility')}
        choices={ROOM_VISIBILITY_CHOICES}
        alwaysOn
        resettable
        emptyText={t('module.socialTrading.filterAll')}
      />
      <SelectInput
        source="smartMoney"
        label={t('module.socialTrading.roomSmartMoneyFilter')}
        choices={SMART_MONEY_FILTER_CHOICES}
        alwaysOn
        resettable
        emptyText={t('module.socialTrading.filterAll')}
      />
      <BooleanInput
        source="includeDeleted"
        label={t('module.socialTrading.includeDeleted')}
        alwaysOn
        sx={{ ml: 2 }}
      />
    </Filter>
  );
};

export default function SocialTradingRoomsList() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { askConfirm, confirmDialog } = useStConfirmAction();
  const [owners, setOwners] = useState<StIdentityUser[]>([]);
  const [kolOwners, setKolOwners] = useState<StIdentityUser[]>([]);
  const [agents, setAgents] = useState<StAgent[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<RoomForm>(emptyForm());
  const [editRoom, setEditRoom] = useState<StRoom | null>(null);
  const [editForm, setEditForm] = useState<RoomForm>(emptyForm());
  const [announcementRoom, setAnnouncementRoom] = useState<StRoom | null>(null);
  const [announcementForm, setAnnouncementForm] = useState({
    announcement: '',
    announcementLang: emptyLangPack(),
    announcementLangDirty: false,
    announcementUpdatedAt: '',
    i18nStale: false,
  });
  const [muteRoom, setMuteRoom] = useState<StRoom | null>(null);
  const [memberMutes, setMemberMutes] = useState<StMemberMute[]>([]);
  const [muteUserId, setMuteUserId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<StRoom | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [avatarDefaults, setAvatarDefaults] = useState<StAvatarDefaults | null>(null);

  const agentById = useMemo(() => {
    const map = new Map<string, StAgent>();
    agents.forEach((a) => map.set(a.agentId, a));
    return map;
  }, [agents]);

  const announcementTimeHelper = (current?: string | null) => {
    const shown = stFormatDateTime(current);
    if (!shown) return t('module.socialTrading.announcementUpdatedAtHint');
    return t('module.socialTrading.announcementUpdatedAtHintWithCurrent', { time: shown });
  };

  useEffect(() => {
    Promise.all([
      stIdentityUsers({ role: 'OFFICIAL', limit: 100, offset: 0 }),
      stIdentityUsers({ role: 'KOL', limit: 100, offset: 0 }),
      stListAgents({ activeOnly: true, limit: 100 }),
    ])
      .then(([ownerRes, kolRes, agentRes]) => {
        setOwners(ownerRes?.items || []);
        setKolOwners(kolRes?.items || []);
        setAgents(agentRes?.items || []);
      })
      .catch((e: any) => notify(stErrorMessage(e), { type: 'error' }));
  }, [notify]);

  useEffect(() => {
    stAvatarDefaults()
      .then((d) => setAvatarDefaults(d))
      .catch(() => setAvatarDefaults(null));
  }, []);

  const openCreate = () => {
    const form = emptyForm();
    if (owners[0]) form.ownerUserId = owners[0].userId;
    setCreateForm(form);
    setCreateOpen(true);
  };

  const validateForm = (form: RoomForm, requireOwner: boolean, isCreate: boolean): string | null => {
    if (requireOwner && !form.ownerUserId.trim()) return t('module.socialTrading.roomNeedFields');
    if (form.ownerUserId.trim() && !UUID_RE.test(form.ownerUserId.trim())) {
      return t('module.socialTrading.roomOwnerInvalid');
    }
    if (requireOwner && kolCannotOwnOfficialRoom(form.type, form.ownerUserId, kolOwners)) {
      return t('module.socialTrading.roomKolCannotOwnOfficial');
    }
    if (!form.name.trim() || !form.tagline.trim() || !form.preview.trim()) {
      return t('module.socialTrading.roomNeedFields');
    }
    if (singleAgentRoomType(form.type) && form.selectedAgents.length !== 1) {
      return t('module.socialTrading.roomAgentRequired');
    }
    const needName = isCreate || form.nameLangDirty;
    const needTagline = isCreate || form.taglineLangDirty;
    const needPreview = isCreate || form.previewLangDirty;
    if (announcementOverBudget(form.announcement)) {
      return t('module.socialTrading.announcementTooLong', {
        used: announcementByteLength(form.announcement),
        max: ANNOUNCEMENT_MAX_BYTES,
      });
    }
    const needAnnouncement = isCreate || form.announcementLangDirty;
    if (needName && !isLangPackComplete(syncZh(form.nameLang, form.name), false)) {
      return t('module.socialTrading.i18nPackIncomplete');
    }
    if (needTagline && !isLangPackComplete(syncZh(form.taglineLang, form.tagline), false)) {
      return t('module.socialTrading.i18nPackIncomplete');
    }
    if (needPreview && !isLangPackComplete(syncZh(form.previewLang, form.preview), false)) {
      return t('module.socialTrading.i18nPackIncomplete');
    }
    if (needAnnouncement && !isLangPackComplete(syncZh(form.announcementLang, form.announcement), true)) {
      return t('module.socialTrading.i18nPackIncomplete');
    }
    return null;
  };

  const createRoom = async () => {
    const err = validateForm(createForm, true, true);
    if (err) {
      notify(err, { type: 'warning' });
      return;
    }
    try {
      await stCreateRoom({
        ownerUserId: createForm.ownerUserId.trim(),
        type: createForm.type,
        name: createForm.name.trim(),
        tagline: createForm.tagline.trim(),
        lang: createForm.lang,
        visibility: createForm.visibility,
        discoverable: createForm.discoverable,
        // Sent exactly as typed: indentation and blank lines are the point.
        announcement: createForm.announcement || undefined,
        avatar: createForm.avatar.trim() || undefined,
        preview: createForm.preview.trim(),
        max: Number(createForm.max) || undefined,
        agentIds: createForm.selectedAgents.map((a) => a.agentId),
        nameLang: syncZh(createForm.nameLang, createForm.name.trim()),
        taglineLang: syncZh(createForm.taglineLang, createForm.tagline.trim()),
        previewLang: syncZh(createForm.previewLang, createForm.preview.trim()),
        announcementLang: syncZh(createForm.announcementLang, createForm.announcement),
      });
      setCreateOpen(false);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      refresh();
    } catch (e: any) {
      const raw = stErrorMessage(e);
      notify(isOwnerKolRoleConflictMessage(raw) ? t('module.socialTrading.roomKolCannotOwnOfficial') : raw, {
        type: 'error',
      });
    }
  };

  const openEdit = (room: StRoom) => {
    setEditRoom(room);
    setEditForm({
      ownerUserId: room.owner || '',
      type: room.type,
      name: room.name || '',
      tagline: room.tagline || '',
      lang: room.lang || 'ZH',
      visibility: room.visibility || 'PUBLIC',
      discoverable: room.discoverable ?? true,
      smartMoney: room.smartMoney ?? false,
      announcement: room.announcement || '',
      announcementUpdatedAt: toLocalInputValue(room.announcementUpdatedAt),
      avatar: room.avatar || '',
      preview: room.preview || '',
      max: String(room.max ?? 500),
      selectedAgents: (room.hostedAgentIds || [])
        .map((id) => agentById.get(id))
        .filter((a): a is StAgent => !!a),
      nameLang: packFromEntries(room.nameLang, room.name || ''),
      taglineLang: packFromEntries(room.taglineLang, room.tagline || ''),
      previewLang: packFromEntries(room.previewLang, room.preview || ''),
      announcementLang: packFromEntries(room.announcementLang, room.announcement || ''),
      nameLangDirty: false,
      taglineLangDirty: false,
      previewLangDirty: false,
      announcementLangDirty: false,
      i18nStale: false,
    });
  };

  const openAnnouncement = (room: StRoom) => {
    setAnnouncementRoom(room);
    setAnnouncementForm({
      announcement: room.announcement || '',
      announcementLang: packFromEntries(room.announcementLang, room.announcement || ''),
      announcementLangDirty: false,
      announcementUpdatedAt: toLocalInputValue(room.announcementUpdatedAt),
      i18nStale: false,
    });
  };

  const saveAnnouncement = async () => {
    if (!announcementRoom) return;
    if (announcementOverBudget(announcementForm.announcement)) {
      notify(
        t('module.socialTrading.announcementTooLong', {
          used: announcementByteLength(announcementForm.announcement),
          max: ANNOUNCEMENT_MAX_BYTES,
        }),
        { type: 'warning' },
      );
      return;
    }
    if (
      announcementForm.announcementLangDirty &&
      !isLangPackComplete(
        syncZh(announcementForm.announcementLang, announcementForm.announcement),
        true,
      )
    ) {
      notify(t('module.socialTrading.i18nPackIncomplete'), { type: 'warning' });
      return;
    }
    try {
      // Compared verbatim: a whitespace-only reformat is a real edit now.
      const announcementChanged =
        announcementForm.announcement !== (announcementRoom.announcement || '');
      await stUpdateRoom({
        roomId: announcementRoom.id,
        ...(announcementChanged ? { announcement: announcementForm.announcement } : {}),
        ...(announcementForm.announcementUpdatedAt.trim()
          ? { announcementUpdatedAt: toISO(announcementForm.announcementUpdatedAt) }
          : {}),
        ...(announcementForm.announcementLangDirty
          ? {
              announcementLang: syncZh(
                announcementForm.announcementLang,
                announcementForm.announcement,
              ),
            }
          : {}),
      });
      setAnnouncementRoom(null);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const saveEdit = async () => {
    if (!editRoom) return;
    const err = validateForm({ ...editForm, type: editRoom.type }, false, false);
    if (err) {
      notify(err, { type: 'warning' });
      return;
    }
    const nextOwner = editForm.ownerUserId.trim();
    const ownerChanged = !!nextOwner && nextOwner !== (editRoom.owner || '');
    if (ownerChanged && kolCannotOwnOfficialRoom(editRoom.type, nextOwner, kolOwners)) {
      notify(t('module.socialTrading.roomKolCannotOwnOfficial'), { type: 'warning' });
      return;
    }
    const announcementChanged =
      editForm.announcement !== (editRoom.announcement || '') || editForm.announcementLangDirty;
    const smartMoneyChanged = editForm.smartMoney !== (editRoom.smartMoney ?? false);
    try {
      await stUpdateRoom({
        roomId: editRoom.id,
        name: editForm.name.trim(),
        tagline: editForm.tagline.trim(),
        lang: editForm.lang,
        visibility: editForm.visibility,
        discoverable: editForm.discoverable,
        ...(smartMoneyChanged ? { smartMoney: editForm.smartMoney } : {}),
        ...(announcementChanged ? { announcement: editForm.announcement } : {}),
        ...(editForm.announcementUpdatedAt.trim()
          ? { announcementUpdatedAt: toISO(editForm.announcementUpdatedAt) }
          : {}),
        avatar: editForm.avatar.trim() || undefined,
        preview: editForm.preview.trim() || undefined,
        max: Number(editForm.max) || undefined,
        agentIds: editForm.selectedAgents.map((a) => a.agentId),
        ...(ownerChanged ? { ownerUserId: nextOwner } : {}),
        ...(editForm.nameLangDirty
          ? { nameLang: syncZh(editForm.nameLang, editForm.name.trim()) }
          : {}),
        ...(editForm.taglineLangDirty
          ? { taglineLang: syncZh(editForm.taglineLang, editForm.tagline.trim()) }
          : {}),
        ...(editForm.previewLangDirty
          ? { previewLang: syncZh(editForm.previewLang, editForm.preview.trim()) }
          : {}),
        ...(editForm.announcementLangDirty
          ? { announcementLang: syncZh(editForm.announcementLang, editForm.announcement) }
          : {}),
      });
      setEditRoom(null);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      refresh();
    } catch (e: any) {
      const raw = stErrorMessage(e);
      const message = isOwnerKolRoleConflictMessage(raw)
        ? t('module.socialTrading.roomKolCannotOwnOfficial')
        : isSmartMoneyRequiresPublicMessage(raw)
          ? t('module.socialTrading.roomSmartMoneyRequiresPublic')
          : raw;
      notify(message, { type: 'error' });
    }
  };

  const deleteRoom = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await stDeleteRoom({ roomId: deleteTarget.id });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setDeleteTarget(null);
      refresh();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const toggleMuteAll = async (room: StRoom) => {
    try {
      await stSetRoomMuteAll(room.id, !room.muteAll);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const openMemberMutes = async (room: StRoom) => {
    setMuteRoom(room);
    setMuteUserId('');
    try {
      const rows = await stRoomMemberMutes(room.id);
      setMemberMutes(rows || []);
    } catch (e: any) {
      setMemberMutes([]);
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const addMemberMute = async () => {
    if (!muteRoom || !UUID_RE.test(muteUserId.trim())) {
      notify(t('module.socialTrading.officialInvalidUserId'), { type: 'warning' });
      return;
    }
    try {
      await stSetRoomMemberMute(muteRoom.id, { userId: muteUserId.trim(), muted: true });
      setMuteUserId('');
      const rows = await stRoomMemberMutes(muteRoom.id);
      setMemberMutes(rows || []);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const clearMemberMute = async (userId: string) => {
    if (!muteRoom) return;
    try {
      await stSetRoomMemberMute(muteRoom.id, { userId, muted: false });
      const rows = await stRoomMemberMutes(muteRoom.id);
      setMemberMutes(rows || []);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const ownerOptionsForType = (type: string) => ownerOptionsForRoomType(type, owners, kolOwners);

  const formFields = (
    form: RoomForm,
    setForm: (f: RoomForm | ((prev: RoomForm) => RoomForm)) => void,
    mode: 'create' | 'edit',
  ) => (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {mode === 'create' && (
        <TextField
          select
          size="small"
          label={t('module.socialTrading.roomType')}
          helperText={t('module.socialTrading.roomTypeHint')}
          required
          value={form.type}
          onChange={(e) => {
            const nextType = e.target.value;
            const nextOwners = ownerOptionsForType(nextType);
            setForm({
              ...form,
              type: nextType,
              ownerUserId: nextOwners[0]?.userId || '',
              selectedAgents:
                singleAgentRoomType(nextType) && form.selectedAgents.length > 1
                  ? form.selectedAgents.slice(0, 1)
                  : form.selectedAgents,
            });
          }}
        >
          <MenuItem value="OFFICIAL_LOBBY">OFFICIAL_LOBBY</MenuItem>
          <MenuItem value="OFFICIAL_AGENT">OFFICIAL_AGENT</MenuItem>
          <MenuItem value="KOL_ROOM">KOL_ROOM</MenuItem>
        </TextField>
      )}
      <Autocomplete
        freeSolo
        size="small"
        options={ownerOptionsForType(form.type)}
        getOptionLabel={identityOwnerLabel}
        value={form.ownerUserId}
        onInputChange={(_, value) => setForm({ ...form, ownerUserId: value })}
        onChange={(_, value) => {
          if (typeof value === 'string') setForm({ ...form, ownerUserId: value });
          else if (value) setForm({ ...form, ownerUserId: value.userId });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('module.socialTrading.roomOwner')}
            required={mode === 'create'}
            helperText={
              form.type === 'KOL_ROOM'
                ? t('module.socialTrading.roomKolOwnerHint')
                : mode === 'create'
                  ? t('module.socialTrading.roomOwnerHint')
                  : t('module.socialTrading.roomOwnerChangeHint')
            }
          />
        )}
      />
      {form.i18nStale && (
        <Alert severity="warning">{t('module.socialTrading.i18nStaleHint')}</Alert>
      )}
      <I18nFieldBlock
        fieldKey="name"
        label={t('module.socialTrading.roomNameLabel')}
        placeholder={t('module.socialTrading.roomNamePlaceholder')}
        required
        primary={form.name}
        onPrimaryChange={(name) => setForm({ ...form, name })}
        pack={form.nameLang}
        onPackChange={(nameLang) => setForm({ ...form, nameLang, nameLangDirty: true, i18nStale: false })}
        onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
        helperText={t('module.socialTrading.roomNameHint')}
      />
      <I18nFieldBlock
        fieldKey="tagline"
        label={t('module.socialTrading.roomTaglineLabel')}
        placeholder={t('module.socialTrading.roomTaglinePlaceholder')}
        required
        primary={form.tagline}
        onPrimaryChange={(tagline) => setForm({ ...form, tagline })}
        pack={form.taglineLang}
        onPackChange={(taglineLang) =>
          setForm({ ...form, taglineLang, taglineLangDirty: true, i18nStale: false })
        }
        onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
        helperText={t('module.socialTrading.roomTaglineHint')}
      />
      <I18nFieldBlock
        fieldKey="preview"
        label={t('module.socialTrading.roomPreview')}
        placeholder={t('module.socialTrading.roomPreviewPlaceholder')}
        required
        primary={form.preview}
        onPrimaryChange={(preview) => setForm({ ...form, preview })}
        pack={form.previewLang}
        onPackChange={(previewLang) =>
          setForm({ ...form, previewLang, previewLangDirty: true, i18nStale: false })
        }
        onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
        helperText={t('module.socialTrading.roomPreviewHint')}
      />
      <TextField
        select
        size="small"
        label={t('module.socialTrading.roomLanguage')}
        helperText={t('module.socialTrading.roomLanguageHint')}
        required
        value={form.lang}
        onChange={(e) => setForm({ ...form, lang: e.target.value })}
      >
        {['ZH', 'EN', 'JA', 'KO', 'OTHER'].map((l) => (
          <MenuItem key={l} value={l}>
            {l}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={t('module.socialTrading.roomVisibility')}
        helperText={t('module.socialTrading.roomVisibilityHint')}
        required
        value={form.visibility}
        onChange={(e) =>
          setForm({
            ...form,
            visibility: e.target.value,
            // Smart Money is PUBLIC-only; un-flag in the same save instead of letting upstream reject it.
            smartMoney: e.target.value === 'PRIVATE' ? false : form.smartMoney,
          })
        }
      >
        <MenuItem value="PUBLIC">PUBLIC</MenuItem>
        <MenuItem value="PRIVATE">PRIVATE</MenuItem>
      </TextField>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.discoverable}
            onChange={(e) => setForm({ ...form, discoverable: e.target.checked })}
          />
        }
        label={t('module.socialTrading.discoverableLabel')}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
        {t('module.socialTrading.discoverableHint')}
      </Typography>
      {mode === 'edit' && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.smartMoney}
                disabled={form.visibility !== 'PUBLIC'}
                onChange={(e) => setForm({ ...form, smartMoney: e.target.checked })}
              />
            }
            label={t('module.socialTrading.roomSmartMoneyLabel')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            {t('module.socialTrading.roomSmartMoneyHint')}
          </Typography>
        </>
      )}
      {announcementEditLocked(editRoom?.announcementEditLockedUntil) && (
        // Informational only: the backend still accepts a whitespace-only
        // reformat while a translation is running.
        <Alert severity="info">{t('module.socialTrading.announcementLockedHint')}</Alert>
      )}
      <I18nFieldBlock
        fieldKey="announcement"
        label={t('module.socialTrading.announcementLabel')}
        helperText={t('module.socialTrading.announcementBytesHint', {
          used: announcementByteLength(form.announcement),
          max: ANNOUNCEMENT_MAX_BYTES,
        })}
        primary={form.announcement}
        onPrimaryChange={(announcement) => setForm({ ...form, announcement })}
        pack={form.announcementLang}
        onPackChange={(announcementLang) =>
          setForm({ ...form, announcementLang, announcementLangDirty: true, i18nStale: false })
        }
        onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
        multiline
        rows={3}
        allowEmptyFill
      />
      {mode === 'edit' && (
        <TextField
          size="small"
          fullWidth
          type="datetime-local"
          label={t('module.socialTrading.announcementUpdatedAt')}
          helperText={announcementTimeHelper(editRoom?.announcementUpdatedAt)}
          InputLabelProps={{ shrink: true }}
          value={form.announcementUpdatedAt}
          onChange={(e) => setForm({ ...form, announcementUpdatedAt: e.target.value })}
        />
      )}
      <SocialTradingAvatarField
        value={form.avatar}
        onChange={(url) => setForm({ ...form, avatar: url })}
        defaultUrls={roomDefaultUrls(avatarDefaults, form.type)}
        uploadPath="/upload"
        label={t('module.socialTrading.roomAvatar')}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
        {t('module.socialTrading.roomAvatarHint')}
      </Typography>
      <TextField
        size="small"
        label={t('module.socialTrading.roomMaxLabel')}
        helperText={t('module.socialTrading.roomMaxHint')}
        placeholder={t('module.socialTrading.roomMaxPlaceholder')}
        value={form.max}
        onChange={(e) => setForm({ ...form, max: e.target.value })}
      />
      {singleAgentRoomType(form.type) ? (
        <Autocomplete
          options={agents}
          value={form.selectedAgents[0] || null}
          getOptionLabel={(option) => option.name || option.agentId}
          isOptionEqualToValue={(a, b) => a.agentId === b.agentId}
          onChange={(_, value) => setForm({ ...form, selectedAgents: value ? [value] : [] })}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('module.socialTrading.roomAgents')}
              required
              helperText={t('module.socialTrading.roomAgentHint')}
            />
          )}
        />
      ) : (
        <Autocomplete
          multiple
          options={agents}
          value={form.selectedAgents}
          getOptionLabel={(option) => option.name || option.agentId}
          isOptionEqualToValue={(a, b) => a.agentId === b.agentId}
          onChange={(_, value) => setForm({ ...form, selectedAgents: value || [] })}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('module.socialTrading.roomAgents')}
              helperText={t('module.socialTrading.roomAgentHint')}
            />
          )}
        />
      )}
    </Stack>
  );

  return (
    <PermissionGuard resource="st-rooms" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.rooms')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.rooms') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => refresh()}>
                {t('module.socialTrading.refresh')}
              </Button>
              {/* POST /social-trading/integration/rooms requires st-rooms:create OR st-room-members:create */}
              <PermissionGuard
                anyOf={[
                  { resource: 'st-rooms', action: 'create' },
                  { resource: 'st-room-members', action: 'create' },
                ]}
                fallback={null}
                loadingFallback={null}
              >
                <Button size="small" variant="contained" onClick={openCreate}>
                  {t('module.socialTrading.createRoom')}
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 2, pb: 2 }}>
          <List
            resource={Resources.socialTradingRooms}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                rowStyle={(record: StRoom) => ({ opacity: record.deletedAt ? 0.55 : 1 })}
              >
                <FunctionField
                  label={t('module.socialTrading.roomName')}
                  render={(room: StRoom) => stText(room.name)}
                />
                <FunctionField label="roomId" render={(room: StRoom) => <StIdCell value={room.id} />} />
                <FunctionField
                  label="groupId(IM)"
                  render={(room: StRoom) => <StIdCell value={room.groupId} />}
                />
                <FunctionField label="type" render={(room: StRoom) => stText(room.type)} />
                <FunctionField label="visibility" render={(room: StRoom) => stText(room.visibility)} />
                <FunctionField
                  label="discoverable"
                  render={(room: StRoom) => <StBooleanChip value={room.discoverable ?? true} />}
                />
                <FunctionField
                  label="smartMoney"
                  render={(room: StRoom) => (
                    <StBooleanChip
                      value={room.smartMoney}
                      trueLabel={t('module.socialTrading.roomSmartMoneyOn')}
                      falseLabel={t('module.socialTrading.roomSmartMoneyOff')}
                    />
                  )}
                />
                <FunctionField
                  label="accessPassword"
                  render={(room: StRoom) => stText(room.accessPassword)}
                />
                <FunctionField
                  label={t('module.socialTrading.roomOwner')}
                  render={(room: StRoom) => <StIdCell value={room.owner || room.ownerLabel} />}
                />
                <FunctionField
                  label="members(agents)"
                  render={(room: StRoom) =>
                    `${room.members ?? 0}(${(room.hostedAgentIds || []).length})`
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.muteAll')}
                  render={(room: StRoom) => (
                    <StBooleanChip
                      value={!!room.muteAll}
                      trueLabel={t('module.socialTrading.muteOn')}
                      falseLabel={t('module.socialTrading.muteOff')}
                    />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.announcementUpdatedAt')}
                  sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
                  render={(room: StRoom) => (
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {stText(stFormatDateTime(room.announcementUpdatedAt))}
                    </Box>
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.roomStatus')}
                  render={(room: StRoom) => (
                    <StStatusChip status={room.deletedAt ? 'DELETED' : 'ACTIVE'} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.actions')}
                  textAlign="right"
                  render={(room: StRoom) => {
                    if (room.deletedAt) return null;
                    return (
                      <StActionRow>
                        {/* POST /social-trading/integration/rooms/update requires st-rooms:update */}
                        <PermissionButton resource="st-rooms" action="update">
                          <StActionButton onClick={() => openEdit(room)}>
                            {t('module.socialTrading.editRoom')}
                          </StActionButton>
                        </PermissionButton>
                        <PermissionButton resource="st-rooms" action="update">
                          <StActionButton onClick={() => openAnnouncement(room)}>
                            {t('module.socialTrading.editAnnouncement')}
                          </StActionButton>
                        </PermissionButton>
                        {/* POST /social-trading/integration/rooms/:id/mute-all requires st-rooms:update */}
                        <PermissionButton resource="st-rooms" action="update">
                          <StActionButton
                            color={room.muteAll ? 'success' : 'warning'}
                            onClick={() =>
                              askConfirm({
                                title: t('module.socialTrading.confirmActionTitle'),
                                content: t('module.socialTrading.confirmActionBody', {
                                  action: room.muteAll
                                    ? t('module.socialTrading.unmuteAll')
                                    : t('module.socialTrading.muteAll'),
                                }),
                                confirmLabel: t('module.socialTrading.confirmOk'),
                                confirmColor: room.muteAll ? 'success' : 'warning',
                                onConfirm: () => toggleMuteAll(room),
                              })
                            }
                          >
                            {room.muteAll
                              ? t('module.socialTrading.unmuteAll')
                              : t('module.socialTrading.muteAll')}
                          </StActionButton>
                        </PermissionButton>
                        <StActionButton onClick={() => openMemberMutes(room)}>
                          {t('module.socialTrading.memberMute')}
                        </StActionButton>
                        {/* POST /social-trading/integration/rooms/delete requires st-rooms:update */}
                        <PermissionButton resource="st-rooms" action="update">
                          <StActionButton color="warning" onClick={() => setDeleteTarget(room)}>
                            {t('module.socialTrading.deleteRoom')}
                          </StActionButton>
                        </PermissionButton>
                      </StActionRow>
                    );
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('module.socialTrading.createRoom')}</DialogTitle>
          <DialogContent>{formFields(createForm, setCreateForm, 'create')}</DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/integration/rooms requires st-rooms:create OR st-room-members:create */}
            <PermissionGuard
              anyOf={[
                { resource: 'st-rooms', action: 'create' },
                { resource: 'st-room-members', action: 'create' },
              ]}
              fallback={null}
              loadingFallback={null}
            >
              <Button variant="contained" onClick={createRoom}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionGuard>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editRoom} onClose={() => setEditRoom(null)} fullWidth maxWidth="md">
          <DialogTitle>{t('module.socialTrading.editRoom')}</DialogTitle>
          <DialogContent>{formFields(editForm, setEditForm, 'edit')}</DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRoom(null)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/integration/rooms/update requires st-rooms:update */}
            <PermissionButton resource="st-rooms" action="update">
              <Button variant="contained" onClick={saveEdit}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!announcementRoom}
          onClose={() => setAnnouncementRoom(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {t('module.socialTrading.editAnnouncement')}
            {announcementRoom ? ` · ${announcementRoom.name}` : ''}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {announcementForm.i18nStale ? (
                <Alert severity="warning">{t('module.socialTrading.i18nStaleHint')}</Alert>
              ) : null}
              <I18nFieldBlock
                fieldKey="announcement"
                label={t('module.socialTrading.roomAnnouncement')}
                primary={announcementForm.announcement}
                onPrimaryChange={(announcement) =>
                  setAnnouncementForm((prev) => ({ ...prev, announcement }))
                }
                pack={announcementForm.announcementLang}
                onPackChange={(announcementLang) =>
                  setAnnouncementForm((prev) => ({
                    ...prev,
                    announcementLang,
                    announcementLangDirty: true,
                    i18nStale: false,
                  }))
                }
                onPrimaryEditedAfterFill={() =>
                  setAnnouncementForm((prev) => ({ ...prev, i18nStale: true }))
                }
                multiline
                rows={4}
                allowEmptyFill
                helperText={
                  <span
                    style={{
                      color:
                        announcementByteLength(announcementForm.announcement) > ANNOUNCEMENT_MAX_BYTES
                          ? '#d32f2f'
                          : 'inherit',
                    }}
                  >
                    {announcementByteLength(announcementForm.announcement)} / {ANNOUNCEMENT_MAX_BYTES}{' '}
                    bytes
                  </span>
                }
              />
              <TextField
                size="small"
                fullWidth
                type="datetime-local"
                label={t('module.socialTrading.announcementUpdatedAt')}
                helperText={announcementTimeHelper(announcementRoom?.announcementUpdatedAt)}
                InputLabelProps={{ shrink: true }}
                value={announcementForm.announcementUpdatedAt}
                onChange={(e) =>
                  setAnnouncementForm((prev) => ({
                    ...prev,
                    announcementUpdatedAt: e.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAnnouncementRoom(null)}>
              {t('module.socialTrading.cancel')}
            </Button>
            {/* POST /social-trading/integration/rooms/update requires st-rooms:update */}
            <PermissionButton resource="st-rooms" action="update">
              <Button
                variant="contained"
                onClick={saveAnnouncement}
                disabled={announcementOverBudget(announcementForm.announcement)}
              >
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog open={!!muteRoom} onClose={() => setMuteRoom(null)} fullWidth maxWidth="sm">
          <DialogTitle>
            {t('module.socialTrading.memberMute')}
            {muteRoom ? ` · ${muteRoom.name}` : ''}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('module.socialTrading.userId')}
                  value={muteUserId}
                  onChange={(e) => setMuteUserId(e.target.value)}
                />
                {/* POST /social-trading/integration/rooms/:id/member-mute requires st-room-members:update */}
                <PermissionButton resource="st-room-members" action="update">
                  <Button variant="contained" onClick={addMemberMute}>
                    {t('module.socialTrading.muteUser')}
                  </Button>
                </PermissionButton>
              </Stack>
              {memberMutes.length > 0 && (
                <StScrollTable>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('module.socialTrading.userId')}</TableCell>
                      <TableCell>mutedAt</TableCell>
                      <TableCell align="right">{t('module.socialTrading.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {memberMutes.map((row) => (
                      <TableRow key={row.userId}>
                        <TableCell><StIdCell value={row.userId} /></TableCell>
                        <TableCell>{stText(stFormatDateTime(row.mutedAt))}</TableCell>
                        <TableCell align="right">
                          <StActionRow>
                            {/* POST /social-trading/integration/rooms/:id/member-mute requires st-room-members:update */}
                            <PermissionButton resource="st-room-members" action="update">
                              <StActionButton
                                onClick={() =>
                                  askConfirm({
                                    title: t('module.socialTrading.confirmActionTitle'),
                                    content: t('module.socialTrading.confirmActionBody', {
                                      action: t('module.socialTrading.unmuteUser'),
                                    }),
                                    confirmLabel: t('module.socialTrading.confirmOk'),
                                    onConfirm: () => clearMemberMute(row.userId),
                                  })
                                }
                              >
                                {t('module.socialTrading.unmuteUser')}
                              </StActionButton>
                            </PermissionButton>
                          </StActionRow>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </StScrollTable>
              )}
              {!memberMutes.length && (
                <Typography variant="body2" color="text.secondary">
                  {t('module.socialTrading.muteDetailsEmpty')}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMuteRoom(null)}>{t('module.socialTrading.close')}</Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => !deleting && setDeleteTarget(null)}
          title={t('module.socialTrading.deleteRoom')}
          content={
            // Only a room with a pool has a cycle to force-settle.
            deleteTarget?.poolEnabled ? (
              <StPoolSettleConfirmContent
                message={t('module.socialTrading.confirmDeleteRoom')}
                settleNote={t('module.socialTrading.confirmDeleteRoomSettle')}
              />
            ) : (
              t('module.socialTrading.confirmDeleteRoom')
            )
          }
          action={
            /* POST /social-trading/integration/rooms/delete requires st-rooms:update */
            <PermissionButton resource="st-rooms" action="update">
              <Button variant="contained" color="error" disabled={deleting} onClick={deleteRoom}>
                {t('module.socialTrading.confirmDelete')}
              </Button>
            </PermissionButton>
          }
        />

        {confirmDialog}
      </DashboardContent>
    </PermissionGuard>
  );
}
